export interface CaptureOptions {
  /** Output width in px (height follows the frame aspect). Defaults to the video's intrinsic width. */
  width?: number
  type?: 'image/png' | 'image/jpeg' | 'image/webp'
  quality?: number
  /** Mirror the output like the on-screen preview (selfie). */
  mirror?: boolean
}

/**
 * Composite the camera frame and the rendered overlay into one image.
 * Call right after `scene.render()` (the WebGL canvas is not preserved between frames).
 */
export async function captureComposite(
  video: HTMLVideoElement,
  overlay: HTMLCanvasElement,
  options: CaptureOptions = {},
): Promise<Blob> {
  const srcW = video.videoWidth || overlay.width
  const srcH = video.videoHeight || overlay.height
  const w = options.width ?? srcW
  const h = Math.round((w * srcH) / srcW)
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const ctx = out.getContext('2d')
  if (!ctx) throw new Error('2D context unavailable')
  if (options.mirror) {
    ctx.translate(w, 0)
    ctx.scale(-1, 1)
  }
  ctx.drawImage(video, 0, 0, w, h)
  ctx.drawImage(overlay, 0, 0, w, h)
  const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, options.type ?? 'image/png', options.quality))
  if (!blob) throw new Error('capture failed')
  return blob
}
